import { useState } from 'react';
import { addJobToCandidate } from '@/components/candidates/kanban/utils/jobOperations';
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

export const useCandidateKanban = (candidateId: string | undefined) => {
  const [isAddJobOpen, setIsAddJobOpen] = useState(false);
  const [isFindJobsModalOpen, setIsFindJobsModalOpen] = useState(false);
  
  // Open the add job dialog
  const handleAddJob = () => {
    setIsAddJobOpen(true);
  };

  // Open the find jobs modal
  const handleFindJobs = () => {
    setIsFindJobsModalOpen(true);
  };
  
  // Add a job to the candidate
  const handleAddJobConfirm = async (jobId: string) => {
    setIsAddJobOpen(false);

    if (!jobId || !candidateId) return;

    await addJobToCandidate(candidateId, jobId);
  };

  // Add multiple jobs to the candidate
  const addMultipleJobs = async (jobIds: string[]) => {
    if (!candidateId || jobIds.length === 0) return;
    
    try {
      // First check if any of these jobs are already matched with the candidate
      const { data: existingMatches, error: checkError } = await supabase
        .from('matches')
        .select('job_id')
        .eq('candidate_id', candidateId)
        .in('job_id', jobIds);
      
      if (checkError) throw checkError;
      
      // Filter out jobs that already have matches
      const existingIds = new Set(existingMatches?.map(match => match.job_id) || []);
      const newJobIds = jobIds.filter(id => !existingIds.has(id));
      
      if (newJobIds.length === 0) {
        toast({
          title: "Info",
          description: "All selected jobs are already matched to this candidate."
        });
        return true;
      }
      
      // Create match entries for each new job
      const matchEntries = newJobIds.map(jobId => ({
        candidate_id: candidateId,
        job_id: jobId,
        score: 0, // Default score
        status: 'matched' // Default status
      }));
      
      const { data, error } = await supabase.from('matches').insert(matchEntries).select('id');
      
      if (error) throw error;

      // Trigger AI analysis for each new match
      for (const match of data) {
        await supabase.functions.invoke('ai-processing?command=push-new-analysis', {
          method: 'POST',
          body: {
            entityId: match.id,
            entityType: "match",
            analysisType: "full-match",
            actionOnComplete: "update-match",
          }
        });
      }

      toast({
        title: "Success",
        description: `${newJobIds.length} job(s) added to candidate`,
      });
      
      return true;
    } catch (error: any) {
      console.error("Error adding multiple jobs:", error);
      toast({
        title: "Error",
        description: "Failed to add jobs to candidate",
        variant: "destructive",
      });
      return false;
    }
  };
  
  return {
    isAddJobOpen,
    setIsAddJobOpen,
    isFindJobsModalOpen,
    setIsFindJobsModalOpen,
    handleAddJob,
    handleAddJobConfirm,
    handleFindJobs,
    addMultipleJobs
  };
}; 